import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "react-final-form";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { FormControl, Grid } from "@mui/material";
import { OtpPopup } from "../../../_uiComponents/common/UiModal";
import { OtpModal } from "../../../_uiComponents/Popups/OtpModal";
import { ConfirmationIcon } from "../../../../styles/img/svg/ConfirmationIcon";
import UiCountdownTimer from "../../../_uiComponents/UiCountdownTimer";
import Validator from "../../../../_helpers/validate.schema";
import { MuiOtpInput } from "mui-one-time-password-input";
import {
  responseCodes,
  authConstants,
  commonConstants,
  TRANSLATION_CONST,
} from "../../../../_redux/_constants";
import {
  selectCustomError,
  showCustomLoading,
  isErrorCodeExist,
} from "../../../../_helpers/support.services";
import { RedWarningIcon } from "../../../../styles/img/svg/RedWarningIcon";
import { hideCustomeError } from "../../../../_redux/_actions";

const validateSchema = () =>
  yup.object().shape({
    otp: yup.number().required("Otp is required."),
  });

export const OtpConfirmModal = (props) => {
  const { t, i18n } = useTranslation();
  const { userDetails } = props;

  const formRef = useRef(null);
  const firstOtpRef = useRef(null);
  const isMountedRef = useRef(true);

  const actionDispatch = useDispatch();

  const [showMainModal, setShowMainModal] = useState(true);

  const [sendOtpMethod, setSendOtpMethod] = useState("");
  const [errMessage, setErrMessage] = useState("");
  const [otpValue, setOtpValue] = useState("");

  const { customErrors, loader } = useSelector((state) => {
    return {
      customErrors: state.errorMessage?.customErrors,
      loader: state.loading,
    };
  });

  const onValidate = async (values) => {
    return await Validator(values, validateSchema);
  };

  const otpConfirm = (formData) => {
    props.otpConfirmRequest({ otp: formData.otp });
  };

  const removeErrors = () => {
    actionDispatch(hideCustomeError(responseCodes.ONBOARDING_OTP_FAILED));
    actionDispatch(hideCustomeError(responseCodes.OTP_FAILED));
    actionDispatch(hideCustomeError(responseCodes.OTP_EXPIRED));
    actionDispatch(hideCustomeError(responseCodes.OTP_REQUEST_LIMIT_REACHED));
    actionDispatch(
      hideCustomeError(
        responseCodes.SAVING_ACCOUNT_OPENING_NOT_ELIGIBLE_ERROR_CODE,
      ),
    );
  };

  const otpResend = (type) => {
    setErrMessage("");

    removeErrors();
    props.otpResendRequest(type);

    if (type === commonConstants.OTP_SEND_METHOD_TYPE_EMAIL) {
      setSendOtpMethod(userDetails?.email);
    } else if (type === commonConstants.OTP_SEND_METHOD_TYPE_SMS) {
      setSendOtpMethod(userDetails?.phone1);
    }
  };

  const closeModal = () => {
    actionDispatch(hideCustomeError(responseCodes.OTP_FAILED));
    actionDispatch(hideCustomeError(responseCodes.ONBOARDING_OTP_FAILED));
    actionDispatch(hideCustomeError(responseCodes.OTP_EXPIRED));
    actionDispatch(hideCustomeError(responseCodes.OTP_REQUEST_LIMIT_REACHED));
    actionDispatch(
      hideCustomeError(
        responseCodes.SAVING_ACCOUNT_OPENING_NOT_ELIGIBLE_ERROR_CODE,
      ),
    );
    props?.closeModal();
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let focusTimer = null;
    if (props.show) {
      focusTimer = setTimeout(() => {
        if (!isMountedRef.current) {
          return;
        }
        const firstInput = firstOtpRef.current?.querySelector("input");
        if (firstInput) firstInput.focus();
      }, 100);
    }

    setSendOtpMethod(
      userDetails?.phone1 && userDetails?.email
        ? userDetails?.phone1
        : userDetails?.email && !userDetails?.phone1
          ? userDetails?.email
          : userDetails?.phone1,
    );

    return () => clearTimeout(focusTimer);
  }, [props.show, userDetails?.phone1, userDetails?.email]);

  useEffect(
    function () {
      if (!isMountedRef.current) {
        return;
      }

      let error = "";

      if (Array.isArray(customErrors) && customErrors.length > 0) {
        const orderedCodes = [
          responseCodes.OTP_FAILED,
          responseCodes.ONBOARDING_OTP_FAILED,
          responseCodes.OTP_EXPIRED,
          responseCodes.SAVING_ACCOUNT_OPENING_NOT_ELIGIBLE_ERROR_CODE,
          responseCodes.OTP_REQUEST_LIMIT_REACHED,
        ];

        // Only consider errors whose statusCode is in orderedCodes
        error = customErrors.find((e) => orderedCodes.includes(e?.statusCode));
      }

      setErrMessage(error?.message);
      if (formRef.current) {
        formRef.current.change("otp", "");
      }

      const firstInput = firstOtpRef.current?.querySelector("input");
      if (firstInput) firstInput.focus();
      setOtpValue("");
    },
    [customErrors],
  );

  useEffect(() => {
    return () => {
      removeErrors();
    };
  }, []);

  return (
    <OtpPopup openModal={props.show} handleClose={closeModal} showCloseIcon>
      <OtpModal
        icon={<ConfirmationIcon />}
        message={t("Enter the 6-Digit OTP sent to", {
          phone: sendOtpMethod,
        })}
        buttonText={t("Ok")}
        buttonOnClick={() => {}}
        className={
          i18n.language === TRANSLATION_CONST?.TAMIL.lan
            ? "df-confirmation-message-popup df-otpmodal df-otp-popup-tam"
            : "df-confirmation-message-popup df-otpmodal"
        }
        buttonClass="df-ui-dark-bg-btn df-w-90-btn"
      >
        <Grid container className="df-otpmodal__otp-input">
          <Form
            onSubmit={otpConfirm}
            validate={onValidate}
            render={({
              handleSubmit,
              values,
              submitting,
              error,
              hasValidationErrors,
              form,
            }) => {
              formRef.current = form;
              return (
                <form className="df-otp-form" ref={firstOtpRef}>
                  <FormControl error={error}>
                    {!isErrorCodeExist(
                      [responseCodes.OTP_REQUEST_LIMIT_REACHED],
                      customErrors,
                    ) ? (
                      <MuiOtpInput
                        // Only allow digits 0-9 in every box
                        validateChar={(value) => /^\d$/.test(value || "")}
                        TextFieldProps={{
                          inputProps: {
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            type: "tel",
                            onInput: (e) => {
                              // Strip any non-digit characters immediately
                              e.target.value = e.target.value.replace(
                                /\D/g,
                                "",
                              );
                            },
                          },
                          onPaste: (e) => {
                            const pasted = e.clipboardData.getData("text");
                            if (!/^\d+$/.test(pasted)) {
                              e.preventDefault();
                            }
                          },
                        }}
                        className="df-input-field df-otp-numbers"
                        length={6}
                        value={values.otp}
                        onChange={(otpValue) => {
                          const onlyDigits = (otpValue || "").replace(
                            /\D/g,
                            "",
                          );
                          form.change("otp", onlyDigits);

                          if (onlyDigits.length === 6) {
                            handleSubmit();
                          }
                        }}
                      />
                    ) : (
                      <div className="df-otp-form-disabled-input-container">
                        {Array.from({ length: 6 }).map((_, index) => (
                          <div
                            key={index}
                            className="df-otp-form-disabled-input"
                          ></div>
                        ))}
                      </div>
                    )}

                    {errMessage &&
                      !showCustomLoading(
                        [authConstants.OTP_CONFIRM_REQUEST],
                        loader.pendingRequests,
                      ) && (
                        <span className="df-error df-error-w-warning">
                          {" "}
                          <RedWarningIcon />
                          {errMessage}
                        </span>
                      )}
                  </FormControl>
                </form>
              );
            }}
          />
        </Grid>
        <UiCountdownTimer
          userDetails={userDetails}
          setSendOtpMethod={setSendOtpMethod}
          otpResend={otpResend}
          timer={props?.countDownTimer}
          hideMainModal={() => setShowMainModal(false)}
          showMainModal={() => setShowMainModal(true)}
        />
      </OtpModal>
    </OtpPopup>
  );
};
